'use client';

import { useEffect, useRef, useState } from 'react';

import clsxm from '../utils/style';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';

import type { FilterOption } from '@bridge/types';
import type { ReactNode } from 'react';

export type ClearableSelectOption = FilterOption<string>;

export interface ClearableSelectProps {
  options: readonly ClearableSelectOption[];
  value: string | null;
  onChange: (value: string | null) => void;
  placeholder?: string;
  disabled?: boolean;
  clearable?: boolean;
  className?: string;
  ariaLabel?: string;
  renderLabel?: (option: ClearableSelectOption) => ReactNode;
}

const CLEAR_SENTINEL = '__clear__';

function getLongestLabel(options: readonly ClearableSelectOption[], placeholder?: string): string {
  const labels = options.map((o) => o.label);
  if (placeholder) labels.push(placeholder);
  return labels.reduce((longest, current) => (current.length > longest.length ? current : longest), '');
}

/**
 * A standalone select with nullable value support and stable width sizing.
 * Measures the longest option label to prevent layout shift on selection change.
 * When clearable (default), the placeholder doubles as the "clear" option.
 */
export function ClearableSelect({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  clearable = true,
  className,
  ariaLabel,
  renderLabel,
}: ClearableSelectProps) {
  const measureRef = useRef<HTMLSpanElement>(null);
  const [minWidth, setMinWidth] = useState<number | undefined>(undefined);

  const longestLabel = getLongestLabel(options, placeholder);

  useEffect(() => {
    if (measureRef.current) {
      setMinWidth(measureRef.current.offsetWidth + 48);
    }
  }, [longestLabel]);

  function handleChange(next: string) {
    onChange(next === CLEAR_SENTINEL ? null : next);
  }

  return (
    <Select disabled={disabled} onValueChange={handleChange} value={value ?? (clearable ? CLEAR_SENTINEL : '')}>
      <span
        className="pointer-events-none invisible absolute whitespace-nowrap text-sm"
        aria-hidden="true"
        ref={measureRef}
      >
        {longestLabel}
      </span>
      <SelectTrigger
        className={clsxm('w-full md:w-auto', className)}
        aria-label={ariaLabel}
        style={minWidth ? { minWidth } : undefined}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {clearable && placeholder && <SelectItem value={CLEAR_SENTINEL}>{placeholder}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {renderLabel ? renderLabel(option) : option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
